import * as React from 'react';
import { useIntl } from 'react-intl';
import { SetterOrUpdater, useRecoilState, useRecoilValue } from 'recoil';
import {
  IRide,
  IRideDetail,
  IRidePlayerSpeedLimitsSettings,
  IRidePlayerTimelineAlarmLayer,
  IRidePlayerTimelineEcoDriveLayer,
  IRidePlayerTimelineEventLayer,
  IRidePlayerTimelineSpeedlimitLayer,
  IRidePosition,
  IRideTimelineLayers,
} from '../types';
import {
  activeRideIndexAtom,
  isRidePlayedAtom,
  pauseAtom,
  playAtom,
  selectedRidesAtom,
  showDataLayerAtom,
  speedlimitSettingsAtom,
  stopAtom,
} from '../RidePlayerState';
import {
  createTimelineMarkerTooltip,
  createTimelinePositionMarker,
} from '../RidePlayerMarkerService';
import {
  createTimelineAlarmLayer,
  createTimelineEcoDriveLayer,
  createTimelineEventLayer,
  createTimelineSpeedlimitLayer,
} from '../RidePlayerService';

interface IUseTimeline {
  timelineLayers: IRideTimelineLayers;
  speedLimitLayer: IRidePlayerTimelineSpeedlimitLayer | null;
  alarmLayer: IRidePlayerTimelineAlarmLayer | null;
  eventLayer: IRidePlayerTimelineEventLayer | null;
  ecoDriveLayer: IRidePlayerTimelineEcoDriveLayer | null;
  handleTimelineTrackRef: (ref: HTMLDivElement | null) => void;
  timelineCursorPosition: number;
  updateTimelinePositionMarker: (
    positionIndex: number,
    position: IRidePosition,
    rideData: IRideDetail
  ) => void;
  hideTimelinePositionMarker: () => void;
  showTimelinePositionMarker: () => void;
  showLayerData: boolean;
  setShowLayerData: SetterOrUpdater<boolean>;
  timelinePositionMarker: google.maps.Marker | null;
  timelinePositionMarkerTooltip: google.maps.OverlayView | null;
}

/**
 * useRidePlayerTimeline custom hook
 * @param {IRide} rides
 * @returns {IRideTimelineLayers}
 */
const useTimeline = (
  map: google.maps.Map,
  rides: IRide[],
  ridePosition: number,
  isLastRidePlayer?: boolean
): IUseTimeline => {
  const intl = useIntl();
  const speedlimitSettings = useRecoilValue<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const timelineTrackRef = React.useRef<HTMLDivElement | null>(null);
  const timelineLayers = React.useRef<IRideTimelineLayers>({});
  // Timeline position marker (show marker base on position on the timeline)
  const timelinePositionMarker = React.useRef<google.maps.Marker | null>(null);
  const timelinePositionMarkerTooltip = React.useRef<google.maps.OverlayView | null>(null);
  const speedLimitLayer = React.useRef<IRidePlayerTimelineSpeedlimitLayer | null>(null);
  const alarmLayer = React.useRef<IRidePlayerTimelineAlarmLayer | null>(null);
  const ecoDriveLayer = React.useRef<IRidePlayerTimelineEcoDriveLayer | null>(null);
  const eventLayer = React.useRef<IRidePlayerTimelineEventLayer | null>(null);
  const activeRideIndex = useRecoilValue<number>(activeRideIndexAtom);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRideNumber = selectedRides[activeRideIndex];
  const maxRidePositions = rides[activeRideNumber - 1]
    ? rides[activeRideNumber - 1].positions.length - 1
    : 0;
  const [cursorPosition, setCursorPosition] = React.useState<number>(0); // +14px as zero position (margin left)
  const stop = useRecoilValue<boolean>(stopAtom);
  const play = useRecoilValue<boolean>(playAtom);
  const pause = useRecoilValue<boolean>(pauseAtom);
  const isRidePlayed = useRecoilValue<boolean>(isRidePlayedAtom);
  const [showLayerData, setShowLayerData] = useRecoilState<boolean>(showDataLayerAtom);

  // Show layer data
  React.useEffect(() => {
    if (isLastRidePlayer && rides.length === 1) {
      setShowLayerData(true);
    }
    if (!isLastRidePlayer && rides.length === 1) {
      setShowLayerData(true);
    }
  }, [isLastRidePlayer, rides, setShowLayerData]);

  // Calculate cursor position in pixels
  React.useEffect(() => {
    if (timelineTrackRef.current) {
      const cursorPos: number =
        timelineTrackRef.current.offsetWidth / (maxRidePositions / ridePosition);
      setCursorPosition(cursorPos);
    }
  }, [maxRidePositions, ridePosition]);

  // Handle timeline track reference for pixel width calculation
  const handleTimelineTrackRef = (ref: HTMLDivElement | null): void => {
    if (ref) {
      timelineTrackRef.current = ref;
    }
  };

  /**
   * Handle position tooltip
   */
  const handleTooltip = (): void => {
    if (timelinePositionMarkerTooltip.current) {
      timelinePositionMarkerTooltip.current.setMap(
        timelinePositionMarkerTooltip.current.getMap() ? null : map
      );
    }
  };

  /**
   * Hide timeline position marker
   */
  const hideTimelinePositionMarker = () => {
    if (timelinePositionMarker.current) {
      timelinePositionMarker.current.setMap(null);
    }
  };

  /**
   *  Show timeline position marker
   */
  const showTimelinePositionMarker = () => {
    if (timelinePositionMarker.current) {
      timelinePositionMarker.current.setMap(map);
    }
  };

  /**
   * Update timeline position marker
   * @param {number} positionIndex
   * @param {IRidePosition} position
   * @param {IRideDetail} rideData
   * @returns {void}
   */
  const updateTimelinePositionMarker = (
    positionIndex: number,
    position: IRidePosition,
    rideData: IRideDetail
  ) => {
    if (timelinePositionMarker.current) {
      if (timelinePositionMarkerTooltip.current) {
        timelinePositionMarkerTooltip.current.setMap(null);
      }
      const newPosition = new google.maps.LatLng(position.gpsLat, position.gpsLon);
      timelinePositionMarker.current.setPosition(newPosition);
      const oldPosition = timelinePositionMarker.current.getPosition();
      if (
        oldPosition &&
        newPosition.lat !== oldPosition.lat &&
        newPosition.lng !== oldPosition.lng &&
        timelinePositionMarkerTooltip.current
      ) {
        timelinePositionMarkerTooltip.current.setMap(null);
      }
      timelinePositionMarkerTooltip.current = createTimelineMarkerTooltip(
        positionIndex,
        position,
        rideData,
        intl
      ) as google.maps.OverlayView;
    }
  };

  // Create timeline position marker after mount
  React.useEffect(() => {
    timelinePositionMarker.current = createTimelinePositionMarker(map, handleTooltip);
    const timelineMarker = timelinePositionMarker.current;
    const timelineMarkerTooltip = timelinePositionMarkerTooltip.current;

    // Empty timeline layer after unmount
    return () => {
      alarmLayer.current = {};
      speedLimitLayer.current = {};
      ecoDriveLayer.current = {};
      eventLayer.current = {};
      if (timelineMarker) {
        timelineMarker.setMap(null);
      }
      if (timelineMarkerTooltip) {
        timelineMarkerTooltip.setMap(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When player is in passive mode, receiving only actual position and without being played,
  // set timeline cursor to vehicle position
  React.useEffect(() => {
    if (!stop && !play && !pause && !isRidePlayed && isLastRidePlayer) {
      if (timelinePositionMarker.current && timelineTrackRef.current) {
        const cursorPos: number = timelineTrackRef.current.offsetWidth;
        setCursorPosition(cursorPos);
      }
    }
  }, [stop, play, pause, rides, isRidePlayed, isLastRidePlayer]);

  // Update timeline layers by ride positions
  // React.useEffect(() => {
  speedLimitLayer.current = createTimelineSpeedlimitLayer(speedlimitSettings, rides);
  alarmLayer.current = createTimelineAlarmLayer(rides);
  ecoDriveLayer.current = createTimelineEcoDriveLayer(rides);
  eventLayer.current = createTimelineEventLayer(rides);
  // }, [rides, speedlimitSettings]);

  const data: IUseTimeline = {
    timelineLayers: timelineLayers.current,
    speedLimitLayer: speedLimitLayer.current,
    alarmLayer: alarmLayer.current,
    ecoDriveLayer: ecoDriveLayer.current,
    eventLayer: eventLayer.current,
    handleTimelineTrackRef,
    timelineCursorPosition: cursorPosition,
    updateTimelinePositionMarker,
    hideTimelinePositionMarker,
    showTimelinePositionMarker,
    showLayerData,
    setShowLayerData,
    timelinePositionMarker: timelinePositionMarker.current,
    timelinePositionMarkerTooltip: timelinePositionMarkerTooltip.current,
  };

  return data;
};

export default useTimeline;
